import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { EditNamePopinComponent } from './edit-name-popin/edit-name-popin.component';
import { BinariesManagementPopinComponent } from './binaries-management-popin/binaries-management-popin.component';
import { MatExpansionPanel } from '@angular/material/expansion';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CopyRecipePopinComponent } from './copy-recipe-popin/copy-recipe-popin.component';
import { Router } from '@angular/router';
import { ReworkRecipePopinComponent } from './rework-recipe-popin/rework-recipe-popin.component';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { ApplianceGroup, DataRef, DataRefsByKey } from '@shared/models/dataref.model';
import { NotificationService } from '@services/notification.service';
import { RefDataService } from '@services/ref-data.service';
import { RecipeService } from '@services/recipe/recipe.service';
import { SharedClientConfigurationService } from '@services/client/sharedClientConfiguration.service';
import { SpecificFields } from '@clients/models/client.model';
import { ChangeTabPopinComponent } from '@shared/components/recipes-form-header/change-tab-popin/change-tab-popin.component';
import { StepLite } from '@shared/models/recipe/step-lite.model';
import { ResourceMediaLite } from '@shared/models/generic/media/resource-media-lite.model';
import { MediaUploadModule } from '@shared/components/media-upload/media-upload.module';
import { CommonModule } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { EditoComponent } from '@shared/components/recipes-form-header/edito/edito.component';
import { SeoComponent } from '@shared/components/recipes-form-header/seo/seo.component';
import { ClassificationsComponent } from '@shared/components/recipes-form-header/classifications/classifications.component';
import { SharedModule } from '@shared/shared.module';
import { BINARY_TARGET_DOMAIN } from '@packs/utils/pack.utils';

@Component({
  selector: 'app-recipes-form-header',
  templateUrl: './recipes-form-header.component.html',
  styleUrls: ['./recipes-form-header.component.scss'],
  imports: [
    TranslateModule,
    MediaUploadModule,
    ReactiveFormsModule,
    CommonModule,
    EditoComponent,
    SeoComponent,
    ClassificationsComponent,
    MatChipsModule,
    SharedModule,
  ],
})
export class RecipesFormHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formRecipe: UntypedFormGroup = this.formBuilder.group({});
  @Input() typeAbaque = false;
  @Input() recipeOriginal?: RecipeLite;
  @Input() variants?: RecipeLite[] | null;
  @Input() isSeoURLDisabled$: BehaviorSubject<boolean>;
  @Input() applianceGroupsRefData?: DataRefsByKey<ApplianceGroup> | null;
  @Input() variantDirty = false;

  @Output() updateRecipe = new EventEmitter<Partial<RecipeLite>>();

  @ViewChild('classificationsPanel') classificationsPanel?: MatExpansionPanel;
  @ViewChild('tabGroup') tabGroup: MatTabGroup;

  math = Math;
  domainsList = [];
  marketsList = [];
  languagesList = [];
  courseTypesList = [];
  partnersList: any[] = [];
  panelClassificationsState = false;
  disableClassificationsFolding = false;
  isReworkAllowed$: Observable<boolean> = of(false);
  isEditSoftHyphenAllowed$: Observable<boolean> = of(false);
  initiatedUpdate = false;

  destroy$ = new Subject<void>();
  displayCTAOldRecipeScreen$: Observable<boolean>;
  showSoftHyphen = false;
  formIsDirty = false;
  previousIndex = 0;
  preventTabChange = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private refDataService: RefDataService,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private recipeService: RecipeService,
    private router: Router,
    private dialog: MatDialog,
    private sharedClientConfigService: SharedClientConfigurationService,
  ) {}

  ngOnInit(): void {
    this.refDataService
      .getByKey('domains', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allDomains) => {
        this.domainsList = this.refDataService.translateReference(this.refDataService.toArray(allDomains));
        // this.domainsObject = allDomains;
      });
    this.refDataService
      .getByKey('markets', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allMarkets) => {
        this.marketsList = this.refDataService.translateReference(this.refDataService.toArray(allMarkets));
      });
    this.refDataService
      .getByKey('languages', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allLanguages) => {
        this.languagesList = this.refDataService.translateReference(this.refDataService.toArray(allLanguages));
      });
    this.refDataService
      .getByKey('courseTypes', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allCourseTypes) => {
        this.courseTypesList = this.refDataService.translateReference(this.refDataService.toArray(allCourseTypes));
      });
    this.refDataService
      .getByKey('partners', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allPartners) => {
        this.partnersList = this.refDataService.toArray(allPartners);
      });
    this.isReworkAllowed$ = this.refDataService.getFeatureFlags('RECIPE_REWORK_ACTIVATION').pipe(
      takeUntil(this.destroy$),
      map((reworkAllowed) =>
        reworkAllowed.activatedAreas.some(
          (area) =>
            area.domain.key === this.recipeOriginal?.domain &&
            area.market.key === this.recipeOriginal?.market &&
            area.lang.key === this.recipeOriginal?.lang,
        ),
      ),
      shareReplay(1),
    );

    this.isEditSoftHyphenAllowed$ = this.refDataService.getFeatureFlags('SOFT_HYPHEN_ACTIVATION').pipe(
      takeUntil(this.destroy$),
      map((reworkAllowed) =>
        reworkAllowed.activatedAreas.some(
          (area) =>
            area.domain.key === this.recipeOriginal?.domain &&
            area.market.key === this.recipeOriginal?.market &&
            area.lang.key === this.recipeOriginal?.lang,
        ),
      ),
      shareReplay(1),
    );

    this.displayCTAOldRecipeScreen$ = this.sharedClientConfigService.specificFields$.pipe(
      takeUntil(this.destroy$),
      map((fields: SpecificFields) => !!fields?.displayCTAOldRecipeScreen),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initiatedUpdate && changes.recipeOriginal) {
      this.classificationsPanel?.close();
      this.initiatedUpdate = false;
    }
  }

  onFormDirtyStateChanged(isDirty: boolean): void {
    this.formIsDirty = isDirty;
  }

  getName(key: string, list: DataRef[]): string {
    const obj: any = (list || []).find((object: any) => object.key === key);
    if (obj) {
      return obj.translatedName;
    }
    return '';
  }

  getNamePartner(key: string, list: DataRef[]): string {
    const obj: any = (list || []).find((object: any) => object.key === key);
    if (obj) {
      return this.refDataService.translateReference(obj.content, this.recipeOriginal?.market, this.recipeOriginal?.lang).translatedName;
    }
    return '';
  }

  editTitles(): void {
    const data = {
      recipeOriginal: this.recipeOriginal,
      isSkeletonRecipe: false,
      isAbacusRecipe: false,
      isEditSoftHyphenAllowed$: this.isEditSoftHyphenAllowed$,
    };
    const dialogRef: any = this.dialog.open(EditNamePopinComponent, {
      data,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((recipeOriginal: RecipeLite) => {
      if (recipeOriginal && this.formRecipe && this.recipeOriginal) {
        this.recipeOriginal.title = recipeOriginal.title;
        this.recipeOriginal.shortTitle = recipeOriginal.shortTitle;
        this.formRecipe.controls.title.setValue(recipeOriginal.title);
        this.formRecipe.controls.shortTitle.setValue(recipeOriginal.shortTitle);
      }
    });
  }

  manageBinaries(): void {
    this.dialog.open(BinariesManagementPopinComponent, {
      minWidth: '25em',
      data: {
        recipeOriginal: this.recipeOriginal,
        variants: this.variants,
        applianceGroupsRefData: this.applianceGroupsRefData,
        recipeDirty: this.formRecipe.dirty || this.variantDirty,
      },
    });
  }

  saveClassifications(recipe: Partial<RecipeLite>): void {
    this.initiatedUpdate = true;
    if (this.recipeOriginal) {
      this.recipeOriginal.partner = recipe.partner ?? '';
      this.formRecipe.controls.partner.setValue(recipe.partner ?? '');
    }
    this.updateRecipe.emit(recipe);
  }

  onAIGeneratedChange(isAIGenerated: boolean): void {
    if (this.recipeOriginal) {
      this.recipeOriginal.isAIGenerated = isAIGenerated;
    }
  }

  saveSeo(recipe: Partial<RecipeLite>): void {
    this.updateRecipe.emit(recipe);
    this.formIsDirty = false;
  }

  saveEdito(recipe: Partial<RecipeLite>): void {
    this.updateRecipe.emit(recipe);
    this.formIsDirty = false;
  }

  beforeTabChange(event: MatTabChangeEvent): void {
    if (this.preventTabChange) {
      this.preventTabChange = false;
      return;
    }

    if (this.formIsDirty) {
      this.preventTabChange = true;
      this.tabGroup.selectedIndex = this.previousIndex;
      this.dialog.open(ChangeTabPopinComponent);
    } else {
      this.previousIndex = event.index;
    }
  }

  closeMediaUpload(): void {
    if (
      this.recipeOriginal &&
      (this.recipeOriginal.resourceMedias !== this.formRecipe.controls.resourceMedias.value ||
        this.formRecipe.controls.resourceMedias.pristine)
    ) {
      this.recipeOriginal.resourceMedias = this.formRecipe.controls.resourceMedias.value ?? [];
      this.recipeOriginal.steps = (this.recipeOriginal?.steps || []).map((stepValue: StepLite) => {
        stepValue.resourceMedias = (stepValue.resourceMedias || []).filter((media) =>
          this.recipeOriginal?.resourceMedias.some((originMedia) => originMedia.identifier === media),
        );
        return stepValue;
      });
      this.recipeService.updateRecipe(this.recipeOriginal).subscribe(
        (recipeUpdated) => {
          this.recipeOriginal = recipeUpdated;
          this.notificationService.sendSuccess(this.translateService.instant('RECIPE_UPDATED'), true);
          if (this.recipeOriginal?.domain === 'PRO_COO') {
            this.notificationService.sendSuccess(this.translateService.instant('BINARIES.INFO_MSG'), true);
          }
        },
        (err) => this.errorManager(err),
      );
    }
  }

  errorManager(err: HttpErrorResponse): void {
    let errorText = null;
    if (err.status && err.status === 403) {
      errorText = this.translateService.instant('RECIPE_UPDATE_FORBIDDEN');
      this.notificationService.sendError(errorText);
    } else {
      this.notificationService.sendAPIError(err);
    }
  }

  mediaUpdateEvent($event: ResourceMediaLite[]): void {
    if (this.recipeOriginal) {
      this.formRecipe.controls.resourceMedias.setValue($event);
      this.formRecipe.controls.resourceMedias.markAsPristine();
      this.closeMediaUpload();
    }
  }

  copyRecipe(): void {
    const data = {
      recipeOriginal: this.recipeOriginal,
      variantList: this.variants,
    };
    const dialogRef: MatDialogRef<CopyRecipePopinComponent> = this.dialog.open(CopyRecipePopinComponent, { data });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.translationRequested && result?.topRecipeId) {
        this.router.navigateByUrl('recipes/edit/translation/' + result.topRecipeId);
      } else if (result?.recipeId) {
        this.router.navigateByUrl('recipes/edit/' + result.recipeId.sourceSystem + '/' + result.recipeId.functionalId);
      }
    });
  }

  reworkRecipe(): void {
    const data = {
      recipeOriginal: this.recipeOriginal,
      variantList: this.variants,
    };
    const dialogRef: MatDialogRef<ReworkRecipePopinComponent> = this.dialog.open(ReworkRecipePopinComponent, { data });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((reworkedRecipe) => {
        if (this.recipeOriginal && reworkedRecipe) {
          this.recipeOriginal.isReworked = true;
          this.recipeOriginal.oldRating = this.recipeOriginal.community.rating;
          this.recipeOriginal.community.rating = undefined;
        }
      });
  }

  toggleSoftHyphen(): void {
    this.showSoftHyphen = !this.showSoftHyphen;
  }

  getTitle(): string {
    const titleValue = this.formRecipe.controls.title.value;

    if (this.showSoftHyphen) {
      return titleValue.replace(/­/g, '<br>');
    } else {
      return titleValue.replace(/<br>/g, '­');
    }
  }

  protected readonly BINARY_TARGET_DOMAIN = BINARY_TARGET_DOMAIN;
}
